import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// Login and Home are the two landing pages (logged-out / logged-in), so they stay in the main bundle.
import LoginScreen from '../components/auth/LoginScreen';
import HomeScreen from '../components/HomeScreen';
import LandingPage from '../components/LandingPage';
import { POLICIES } from '../config/policies';
import ProtectedRoute from './ProtectedRoute';
import LoadingScreen from '../components/common/LoadingScreen';

const CHUNK_RELOAD_KEY = 'vk-chunk-reload';
const pageLoaders = [];

/**
 * React.lazy for a page, plus:
 * - one automatic page reload if its chunk fails to load (a tab opened before a deploy
 *   asks for chunk files that no longer exist),
 * - registration for background prefetch after first paint.
 */
const lazyPage = (factory) => {
  const load = () =>
    factory()
      .then((mod) => {
        try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch (e) { /* storage unavailable */ }
        return mod;
      })
      .catch((error) => {
        let alreadyReloaded = true;
        try {
          alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1';
          if (!alreadyReloaded) sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
        } catch (e) { /* storage unavailable: don't risk a reload loop */ }
        if (!alreadyReloaded) {
          window.location.reload();
          return new Promise(() => {}); // keep the loading screen until the reload happens
        }
        throw error;
      });
  pageLoaders.push(factory); // prefetch uses the raw import: a failed prefetch must not trigger a reload
  return lazy(load);
};

const SearchScreen = lazyPage(() => import('../components/SearchScreen'));
const SchoolPage = lazyPage(() => import('../components/SchoolPage'));
const GradeSectionsPage = lazyPage(() => import('../components/GradeSectionsPage'));
const GradeBooksPage = lazyPage(() => import('../components/GradeBooksPage'));
const CartPage = lazyPage(() => import('../components/CartPage'));
const CheckoutPage = lazyPage(() => import('../components/CheckoutPage'));
const ProfilePage = lazyPage(() => import('../components/ProfilePage'));
const ProfileCompletePage = lazyPage(() => import('../components/ProfileCompletePage'));
const ShippingAddressesPage = lazyPage(() => import('../components/ShippingAddressesPage'));
const ContactUsPage = lazyPage(() => import('../components/ContactUsPage'));
const StudentsPage = lazyPage(() => import('../components/StudentsPage'));
const OrderHistoryPage = lazyPage(() => import('../components/OrderHistoryPage'));
const OrderDetailsPage = lazyPage(() => import('../components/OrderDetailsPage'));
const PolicyPage = lazyPage(() => import('../components/PolicyPage'));
const SchoolOrdersPage = lazyPage(() => import('../components/SchoolOrdersPage'));

const AppRoutes = () => {
  const { isLoggedIn, authReady, justLoggedOut } = useAuth();

  // Once the first page is up, fetch the other page chunks in the background so later
  // navigation doesn't flash the loading screen. Failures are ignored (retried on demand).
  useEffect(() => {
    const prefetch = () => pageLoaders.forEach((factory) => factory().catch(() => {}));
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(prefetch, { timeout: 4000 });
      return () => window.cancelIdleCallback && window.cancelIdleCallback(id);
    }
    const t = setTimeout(prefetch, 2000);
    return () => clearTimeout(t);
  }, []);

  if (!authReady) {
    return <LoadingScreen />;
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
    <Routes>
      <Route
        path="/login"
        element={isLoggedIn ? <Navigate to="/" replace /> : <LoginScreen />}
      />
      <Route path="/register" element={<Navigate to="/login" replace />} />

      <Route
        path="/"
        element={isLoggedIn ? <HomeScreen /> : <LandingPage />}
      />
      <Route
        path="/search"
        element={
          <SearchScreen />
        }
      />
      <Route
        path="/cart"
        element={
          <ProtectedRoute>
            <CartPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/complete"
        element={
          <ProtectedRoute>
            <ProfileCompletePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/shipping-addresses"
        element={
          <ProtectedRoute>
            <ShippingAddressesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/contact-us"
        element={
          <ProtectedRoute>
            <ContactUsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/students"
        element={
          <ProtectedRoute>
            <StudentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/school/:schoolId"
        element={
          <SchoolPage />
        }
      />
      <Route
        path="/grade/:gradeId/sections"
        element={
          <GradeSectionsPage />
        }
      />
      <Route
        path="/grade/:gradeId"
        element={
          <GradeBooksPage />
        }
      />
      <Route
        path="/checkout"
        element={
          <ProtectedRoute>
            <CheckoutPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <OrderHistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:orderId"
        element={
          <ProtectedRoute>
            <OrderDetailsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/school-orders"
        element={
          <ProtectedRoute>
            <SchoolOrdersPage />
          </ProtectedRoute>
        }
      />

      {/* Policy pages are public so payment gateways and signed-out visitors can read them */}
      {POLICIES.map((p) => (
        <Route key={p.slug} path={p.path} element={<PolicyPage slug={p.slug} />} />
      ))}

      <Route
        path="*"
        element={<Navigate to={isLoggedIn || justLoggedOut ? '/' : '/login'} replace />}
      />
    </Routes>
    </Suspense>
  );
};

export default AppRoutes;
